/**
 * eStream WebTransport Client
 *
 * Provides the same wire protocol as native UDP clients, but over
 * WebTransport (HTTP/3 datagrams) for browser environments.
 *
 * Design:
 * - Datagrams for all messages (same as UDP)
 * - No connection state management (reliability in lattice)
 * - Automatic reconnection with exponential backoff
 */
import { PacketType, HEADER_SIZE, encodeHeader, decodeHeader, encodeSubscribe, encodeEmit, decodeEvent, } from './wire.js';
/**
 * eStream WebTransport client for browsers
 */
export class EstreamTransport extends EventTarget {
    options;
    transport = null;
    subscriptions = new Map();
    subscriptionsByTopic = new Map();
    nextSubscriptionId = 1;
    reconnectAttempts = 0;
    reconnectTimer = null;
    datagramReader = null;
    datagramWriter = null;
    _state = 'disconnected';
    constructor(options) {
        super();
        this.options = {
            sessionToken: new Uint8Array(32),
            autoReconnect: true,
            reconnectDelay: 1000,
            maxReconnectDelay: 30000,
            ...options,
        };
    }
    /** Current connection state */
    get state() {
        return this._state;
    }
    /** Whether currently connected */
    get isConnected() {
        return this._state === 'connected';
    }
    /**
     * Connect to the edge node
     */
    async connect() {
        if (this._state === 'connected' || this._state === 'connecting') {
            return;
        }
        this.setState('connecting');
        try {
            // Check WebTransport support
            if (!('WebTransport' in window)) {
                throw new Error('WebTransport not supported in this browser');
            }
            console.log(`[eStream] Connecting to ${this.options.url}`);
            this.transport = new WebTransport(this.options.url);
            await this.transport.ready;
            console.log('[eStream] WebTransport connected');
            // Set up datagram handling
            this.datagramReader = this.transport.datagrams.readable.getReader();
            this.datagramWriter = this.transport.datagrams.writable.getWriter();
            // Start reading datagrams
            this.readDatagrams();
            // Handle connection close
            this.transport.closed.then(() => {
                console.log('[eStream] Connection closed');
                this.handleDisconnect();
            }).catch((e) => {
                console.error('[eStream] Connection error:', e);
                this.handleDisconnect();
            });
            this.setState('connected');
            this.reconnectAttempts = 0;
            // Re-subscribe to all topics
            for (const sub of this.subscriptions.values()) {
                await this.sendSubscribe(sub.topic);
            }
        }
        catch (e) {
            console.error('[eStream] Connection failed:', e);
            this.setState('error');
            this.scheduleReconnect();
        }
    }
    /**
     * Disconnect from the edge node
     */
    async disconnect() {
        this.options.autoReconnect = false;
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        if (this.transport) {
            try {
                this.transport.close();
            }
            catch (e) {
                // Ignore close errors
            }
            this.transport = null;
        }
        this.datagramReader = null;
        this.datagramWriter = null;
        this.setState('disconnected');
    }
    /**
     * Subscribe to a lattice topic
     *
     * Supports wildcards:
     * - `/circuits/*` matches `/circuits/pending`, `/circuits/completed`
     * - `/governance/#` matches any depth under `/governance/`
     */
    async subscribe(topic, callback) {
        const id = this.nextSubscriptionId++;
        const subscription = {
            id,
            topic,
            callback,
        };
        this.subscriptions.set(id, subscription);
        this.subscriptionsByTopic.set(topic, subscription);
        if (this.isConnected) {
            await this.sendSubscribe(topic);
        }
        return id;
    }
    /**
     * Unsubscribe from a topic
     */
    async unsubscribe(subscriptionId) {
        const sub = this.subscriptions.get(subscriptionId);
        if (!sub)
            return;
        this.subscriptions.delete(subscriptionId);
        this.subscriptionsByTopic.delete(sub.topic);
        if (this.isConnected) {
            await this.sendUnsubscribe(sub.topic);
        }
    }
    /**
     * Emit an event to a lattice topic
     */
    async emit(topic, data) {
        if (!this.isConnected || !this.datagramWriter) {
            throw new Error('Not connected');
        }
        const payload = encodeEmit(topic, data);
        const header = encodeHeader(PacketType.Emit, payload.length);
        const packet = new Uint8Array(HEADER_SIZE + payload.length);
        packet.set(header, 0);
        packet.set(payload, HEADER_SIZE);
        await this.datagramWriter.write(packet);
    }
    /**
     * Set session token (from Spark authentication)
     */
    setSessionToken(token) {
        if (token.length !== 32) {
            throw new Error('Session token must be 32 bytes');
        }
        this.options.sessionToken = token;
    }
    // ========================================================================
    // Private methods
    // ========================================================================
    setState(state) {
        if (this._state === state)
            return;
        this._state = state;
        this.dispatchEvent(new CustomEvent('statechange', { detail: state }));
    }
    async sendSubscribe(topic) {
        if (!this.datagramWriter)
            return;
        const payload = encodeSubscribe(this.options.sessionToken, topic);
        const header = encodeHeader(PacketType.Subscribe, payload.length);
        const packet = new Uint8Array(HEADER_SIZE + payload.length);
        packet.set(header, 0);
        packet.set(payload, HEADER_SIZE);
        await this.datagramWriter.write(packet);
    }
    async sendUnsubscribe(topic) {
        if (!this.datagramWriter)
            return;
        const payload = encodeSubscribe(this.options.sessionToken, topic);
        const header = encodeHeader(PacketType.Unsubscribe, payload.length);
        const packet = new Uint8Array(HEADER_SIZE + payload.length);
        packet.set(header, 0);
        packet.set(payload, HEADER_SIZE);
        await this.datagramWriter.write(packet);
    }
    async readDatagrams() {
        if (!this.datagramReader)
            return;
        try {
            while (true) {
                const { value, done } = await this.datagramReader.read();
                if (done)
                    break;
                this.handleDatagram(value);
            }
        }
        catch (e) {
            console.warn('[eStream] Datagram read error:', e);
        }
    }
    handleDatagram(data) {
        const header = decodeHeader(data);
        if (!header)
            return;
        const payload = data.slice(HEADER_SIZE, HEADER_SIZE + header.payloadLen);
        switch (header.packetType) {
            case PacketType.Event: {
                const event = decodeEvent(payload);
                if (event) {
                    this.deliverEvent(event);
                }
                break;
            }
            case PacketType.Subscribed: {
                // Subscription confirmed
                if (payload.length >= 4) {
                    const view = new DataView(payload.buffer, payload.byteOffset);
                    const subId = view.getUint32(0, true);
                    console.log(`[eStream] Subscription ${subId} confirmed`);
                }
                break;
            }
            case PacketType.SessionPong: {
                // Session keepalive response
                if (payload.length >= 16) {
                    const view = new DataView(payload.buffer, payload.byteOffset);
                    const serverTime = Number(view.getBigUint64(0, true));
                    const expiresAt = Number(view.getBigUint64(8, true));
                    console.log(`[eStream] Session pong: expires ${new Date(expiresAt)}`);
                }
                break;
            }
            case PacketType.SessionExpired: {
                console.warn('[eStream] Session expired');
                this.dispatchEvent(new CustomEvent('sessionexpired'));
                break;
            }
            default:
                console.log(`[eStream] Unknown packet type: 0x${header.packetType.toString(16)}`);
        }
    }
    deliverEvent(event) {
        // Find matching subscriptions
        for (const sub of this.subscriptions.values()) {
            if (this.topicMatches(sub.topic, event.topic)) {
                try {
                    sub.callback(event);
                }
                catch (e) {
                    console.error(`[eStream] Subscription callback error:`, e);
                }
            }
        }
    }
    topicMatches(pattern, topic) {
        if (pattern === topic)
            return true;
        // Single-level wildcard
        if (pattern.endsWith('/*')) {
            const prefix = pattern.slice(0, -1);
            return topic.startsWith(prefix) && !topic.slice(prefix.length).includes('/');
        }
        // Multi-level wildcard
        if (pattern.endsWith('/#')) {
            const prefix = pattern.slice(0, -2);
            return topic.startsWith(prefix);
        }
        return false;
    }
    handleDisconnect() {
        this.transport = null;
        this.datagramReader = null;
        this.datagramWriter = null;
        if (this._state !== 'disconnected') {
            this.setState('disconnected');
            this.scheduleReconnect();
        }
    }
    scheduleReconnect() {
        if (!this.options.autoReconnect)
            return;
        const delay = Math.min(this.options.reconnectDelay * Math.pow(2, this.reconnectAttempts), this.options.maxReconnectDelay);
        console.log(`[eStream] Reconnecting in ${delay}ms...`);
        this.reconnectTimer = setTimeout(() => {
            this.reconnectAttempts++;
            this.connect();
        }, delay);
    }
}
//# sourceMappingURL=transport.js.map